/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect, useRef, useState } from 'react';

// Store
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';

// Utils
import {
	cloneDeep,
	isValidObjectId,
} from '@utils/functions';

// I18n
import { useUploadMedia } from 'shared/api/upload-media';

// Apis
import { useRemoveMedia } from 'shared/api/remove-media';
import { useAddMeasurabilityProject } from '../api/create-measurability-project';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

export const useMeasurabilityProjectCreate = (props: {
	form: any;
}) => {
	const viewport = useRef<HTMLDivElement>(null);

	/**************************** Store ****************************/
	const {
		idsLogoToDelete,
		setIdsLogoToDelete,
		idsCoverUrlToDelete,
		setIdsCoverUrlToDelete,
		idsPublicTechnicalDocumentsToDelete,
		setIdsPublicTechnicalDocumentsToDelete,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		setAddMeasurabilityProjectFormOpened,
		setParamsFromProject,
		measurabilitiesPage,
		measurabilitiesPageSize,
		measurabilitiesQueryParams,
		measurabilitiesSortParams,
	} = useStore();

	/**************************** States ****************************/
	const [producerId, setProducerId] = useState('');
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);

	const [logoValue, setLogoValue] = useState<any>(null);
	const [coverValue, setCoverUrlValue] =
		useState<any>(null);

	const [
		isLoadingPublicTechnicalDocuments,
		setIsLoadingPublicTechnicalDocuments,
	] = useState(false);

	/**************************** I18n ****************************/
	const { t } = useTranslation();

	/**************************** APIs ****************************/
	const removeMedia = useRemoveMedia();

	const uploadMediaLogo = useUploadMedia();

	const uploadMediaCoverUrl = useUploadMedia();

	const addMeasurabilityProject =
		useAddMeasurabilityProject(
			measurabilitiesPage,
			measurabilitiesPageSize,
			measurabilitiesQueryParams,
			measurabilitiesSortParams
		);

	/* ******************************** HELPERS *********************************** */

	const handleMediaCoverUrlValue = (
		idMedia: string,
		media: any
	) => {
		if (!idMedia) {
			return null;
		}
		if (idMedia && media && !coverValue) {
			return new File([''], media.filename);
		} else if (idMedia && media && coverValue) {
			return coverValue;
		} else if (idMedia && !media && coverValue) {
			return coverValue;
		}
	};

	const handleMediaLogoValue = (
		idMedia: string,
		media: any
	) => {
		if (!idMedia) {
			return null;
		}
		if (idMedia && media && !logoValue) {
			return new File([''], media.filename);
		} else if (idMedia && media && logoValue) {
			return logoValue;
		} else if (idMedia && !media && logoValue) {
			return logoValue;
		}
	};

	function convertArrayTechnologiesToLabelValue(
		array: any
	) {
		return array.map((item: any) => ({
			label: t(item.label),
			value: item.value,
		}));
	}

	const handleSubmitForm = async (form: any) => {
		form.validate();

		if (form.isValid()) {
			const {
				name,
				entity,
				unit,
				sellPrice_europertco2eq,
				costPrice_europertco2eq,
				countryCode,
				place,
				cover,
				logo,
				technology,
				twitter,
				linkedin,
				description,
				technicalDocuments,
				website,
				supportedBy,
			} = form.getValues();

			const newIdsCoverUrlToDelete =
				idsCoverUrlToDelete.filter(
					(id: string) => id !== cover
				);
			setIdsCoverUrlToDelete(newIdsCoverUrlToDelete);

			const newIdsLogoToDelete = idsLogoToDelete.filter(
				(id: string) => id !== cover
			);
			setIdsLogoToDelete(newIdsLogoToDelete);

			const twitterParts = twitter
				? twitter.split('/')
				: [];
			const twitterCompanyName =
				twitterParts[twitterParts.length - 1];

			const linkedInCompanyName = linkedin
				? linkedin.split('/company/')[1]
				: '';

			const bodyAddMeasurabilityProject = {
				name,
				entity,
				unit,
				...(!isNil(supportedBy) ? { supportedBy } : {}),
				sellPrice_europertco2eq,
				costPrice_europertco2eq,
				location: {
					countryCode,
					place,
				},
				cover,
				...(logo ? { logo } : {}),
				technology,
				description,
				...(twitter || linkedin || website
					? {
							social: {
								...(twitter
									? { twitter: twitterCompanyName }
									: {}),
								...(linkedin
									? {
											linkedIn: linkedInCompanyName,
										}
									: {}),
								...(website
									? {
											website: website,
										}
									: {}),
							},
						}
					: {}),

				...(technicalDocuments &&
				Array.isArray(technicalDocuments) &&
				technicalDocuments.length > 0
					? {
							publicSpecs: technicalDocuments.map(
								(item: any) => item.id
							),
						}
					: {}),
			};

			addMeasurabilityProject.mutate(
				bodyAddMeasurabilityProject
			);

			if (
				idsLogoToDelete &&
				Array.isArray(idsLogoToDelete) &&
				idsLogoToDelete.length > 0
			) {
				const uniqueIds = [...new Set(idsLogoToDelete)];

				await Promise.all(
					uniqueIds
						.filter((id: string) => id !== logo)
						.map(async (id: string) => {
							if (isValidObjectId(id)) {
								removeMedia.mutate(id);
							}
						})
				);
			}

			const cloneIdsCoverUrlToDelete = cloneDeep(
				idsCoverUrlToDelete
			);

			if (
				idsCoverUrlToDelete &&
				Array.isArray(idsCoverUrlToDelete) &&
				idsCoverUrlToDelete.length > 0
			) {
				const uniqueIds = [
					...new Set(cloneIdsCoverUrlToDelete),
				];
				await Promise.all(
					uniqueIds
						.filter((id: any) => id !== cover)
						.map(async (id: any) => {
							if (isValidObjectId(id)) {
								removeMedia.mutate(id);
							}
						})
				);
			}

			const cloneIdsPublicTechnicalDocumentsToDelete =
				cloneDeep(idsPublicTechnicalDocumentsToDelete);

			const parsedPublicTechnicalDocuments =
				technicalDocuments &&
				Array.isArray(technicalDocuments) &&
				technicalDocuments.length > 0
					? technicalDocuments.map((item: any) => item.id)
					: [];
			if (
				idsPublicTechnicalDocumentsToDelete &&
				Array.isArray(
					idsPublicTechnicalDocumentsToDelete
				) &&
				idsPublicTechnicalDocumentsToDelete.length > 0
			) {
				const uniqueIds = [
					...new Set(
						cloneIdsPublicTechnicalDocumentsToDelete
					),
				];
				await Promise.all(
					uniqueIds
						.filter(
							(id: any) =>
								!parsedPublicTechnicalDocuments.includes(
									id
								)
						)
						.map(async (id: any) => {
							if (isValidObjectId(id)) {
								removeMedia.mutate(id);
							}
						})
				);
			}

			setIdsCoverUrlToDelete([]);
			setIdsLogoToDelete([]);
			setIdsPublicTechnicalDocumentsToDelete([]);
		}
	};

	const handleFileLogo = (
		file: File | null,
		form: any
	) => {
		if (file) {
			const formData = new FormData();
			formData.append('entity', form.getValues().entity);
			formData.append('public', 'true');
			formData.append('file', file);
			uploadMediaLogo.mutate(formData);

			setLogoValue(file);
		} else {
			const idLogo = form.getValues().logo;

			const newIdsLogoToDelete = [
				...idsLogoToDelete,
				idLogo,
			];

			setIdsLogoToDelete(newIdsLogoToDelete);

			form.setValues({
				logo: null,
			});
		}
	};

	const handleFileCoverUrl = (
		file: File | null,
		form: any
	) => {
		if (file) {
			const formData = new FormData();
			formData.append('entity', form.getValues().entity);
			formData.append('public', 'true');
			formData.append('file', file);
			uploadMediaCoverUrl.mutate(formData);
			setCoverUrlValue(file);
		} else {
			const idCoverUrl = form.getValues().cover;
			const newIdsCoverUrlToDelete = [
				...idsCoverUrlToDelete,
				idCoverUrl,
			];
			setIdsCoverUrlToDelete(newIdsCoverUrlToDelete);
			form.setValues({
				cover: null,
			});
		}
	};

	const handleReloadFiles = (
		filteredFiles: any,
		form: any
	) => {
		form
			.getInputProps('technicalDocuments')
			.onChange(filteredFiles);
	};

	const handleUploadFiles = (files: any, form: any) => {
		form
			.getInputProps('technicalDocuments')
			.onChange(files);

		const ids = files.map(
			(item: { filename: string; id: string }) => item.id
		);

		const newIdsPublicTechnicalDocumentsToDelete = [
			...idsPublicTechnicalDocumentsToDelete,
			...ids,
		];
		setIdsPublicTechnicalDocumentsToDelete(
			newIdsPublicTechnicalDocumentsToDelete
		);
	};

	const handleChangeEntity = (
		value: string,
		form: any
	) => {
		form.setFieldValue('entity', value);
		form.setFieldValue('project', null);
		setProducerId(value);
	};

	const handleCloseDrawerAddMeasurabilityProject = async (
		form: any
	) => {
		setAddMeasurabilityProjectFormOpened(false);
		setIsOpenModalCancel(false);
		setParamsFromProject({});
		form.reset();

		if (
			idsLogoToDelete &&
			Array.isArray(idsLogoToDelete) &&
			idsLogoToDelete.length > 0
		) {
			const uniqueIds = [...new Set(idsLogoToDelete)];
			await Promise.all(
				uniqueIds.map(async (id) => {
					if (isValidObjectId(id)) {
						removeMedia.mutate(id);
					}
				})
			);
		}

		if (
			idsCoverUrlToDelete &&
			Array.isArray(idsCoverUrlToDelete) &&
			idsCoverUrlToDelete.length > 0
		) {
			const uniqueIds = [...new Set(idsCoverUrlToDelete)];
			await Promise.all(
				uniqueIds.map(async (id) => {
					if (isValidObjectId(id)) {
						removeMedia.mutate(id);
					}
				})
			);
		}

		if (
			idsPublicTechnicalDocumentsToDelete &&
			Array.isArray(
				idsPublicTechnicalDocumentsToDelete
			) &&
			idsPublicTechnicalDocumentsToDelete.length > 0
		) {
			const uniqueIds = [
				...new Set(idsPublicTechnicalDocumentsToDelete),
			];
			await Promise.all(
				uniqueIds.map(async (id) => {
					if (isValidObjectId(id)) {
						removeMedia.mutate(id);
					}
				})
			);
		}

		setIdsCoverUrlToDelete([]);
		setIdsLogoToDelete([]);
		setIdsPublicTechnicalDocumentsToDelete([]);
	};
	/********************************************************************************************/
	useEffect(() => {
		if (uploadMediaLogo.isSuccess) {
			if (
				uploadMediaLogo.data &&
				uploadMediaLogo.data.id
			) {
				props.form.setValues({
					logo: uploadMediaLogo.data.id,
				});

				const idLogo = uploadMediaLogo.data.id;

				const newIdsLogoToDelete = [
					...idsLogoToDelete,
					idLogo,
				];

				setIdsLogoToDelete(newIdsLogoToDelete);
			}
		}
	}, [uploadMediaLogo.isSuccess]);

	useEffect(() => {
		if (uploadMediaCoverUrl.isSuccess) {
			if (
				uploadMediaCoverUrl.data &&
				uploadMediaCoverUrl.data.id
			) {
				props.form.setValues({
					cover: uploadMediaCoverUrl.data.id,
				});

				const idCoverUrl = uploadMediaCoverUrl.data.id;
				const newIdsCoverUrlToDelete = [
					...idsCoverUrlToDelete,
					idCoverUrl,
				];
				setIdsCoverUrlToDelete(newIdsCoverUrlToDelete);
			}
		}
	}, [uploadMediaCoverUrl.isSuccess]);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	/*
	useEffect(() => {
		if (
			props &&
			props.form &&
			props.form.values &&
			props.form.values.project
		) {
			const projectId = props.form.values.project;
			if (projectId) {
				const foundCountry = projects.find(
					(_project: TMesurabilityProject) =>
						_project.id === projectId
				);
				if (
					foundCountry &&
					foundCountry.location &&
					foundCountry.location.place
				) {
					props.form.setValues({
						place: foundCountry.location.place,
					});
				}

				if (
					foundCountry &&
					foundCountry.location &&
					foundCountry.location.countryCode
				) {
					props.form.setValues({
						country: foundCountry.location.countryCode,
					});
				}
			}
		}
	}, [
		props && props.form
			? props.form.values.project
			: null,
	]);
	*/
	/********************************************************************************************/
	return {
		handleCloseDrawerAddMeasurabilityProject,
		handleChangeEntity,
		idsLogoToDelete,
		setIdsLogoToDelete,
		idsCoverUrlToDelete,
		setIdsCoverUrlToDelete,
		idsPublicTechnicalDocumentsToDelete,
		setIdsPublicTechnicalDocumentsToDelete,
		producerId,
		setProducerId,
		logoValue,
		setLogoValue,
		coverValue,
		setCoverUrlValue,
		isLoadingPublicTechnicalDocuments,
		setIsLoadingPublicTechnicalDocuments,
		removeMedia,
		uploadMediaLogo,
		uploadMediaCoverUrl,
		handleMediaCoverUrlValue,
		handleMediaLogoValue,
		convertArrayTechnologiesToLabelValue,
		handleSubmitForm,
		handleFileLogo,
		handleFileCoverUrl,
		handleReloadFiles,
		handleUploadFiles,
		viewport,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		addMeasurabilityProject,
		isOpenModalCancel,
		setIsOpenModalCancel,
	};
};
